import React, { useEffect, useMemo, useState } from "react";
import { useNotification } from "../../context/NotificationContext";
import { dateConverter, getAvatarName } from "../../Helpers";
import { CandidateComment, RoleStatus } from "../../Models";
import useApiToken from "../../hooks/useApiToken";
import { useParams } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import { CheckIcon, TrashIcon, XMarkIcon } from "@heroicons/react/20/solid";
import Avatar from "../Shared/Avatar";
import { Spinner } from "../Shared/Spinner";
import useGraphApiToken from "../../hooks/useGraphApiToken";
import useUserData from "../../hooks/useUserData";
import SelectRoleStatus from "./SelectRoleStatus";
import { CreateCandidateComment, DeleteComment, GetCandidateComments } from "../../api/candidates.api";
import { GetStatusForRole } from "../../api/roles.api";
import { GetProfilePhoto, GetUserName } from "../../api/microsoft.api";

type comment = {
  personId: string;
  id: string;
  name: string;
  date: string;
  roleStatusId?: string;
  imageSrc: string | undefined;
  body: string;
};

type props = {
  candidateId?: string;
  roleId?: string;
  roleStatusId?: string;
  initialCommentRoleStatusId?: string;
};

export default function CandidateNotes({
  candidateId,
  roleId,
  roleStatusId,
}: props) {
  const [note, setNote] = useState<string>("");
  const [candidateComments, setCandidateComments] = useState<CandidateComment[]>([]);
  const [deleteCommentConfirm, setDeleteCommentConfirm] = useState("");
  const { success } = useNotification();
  const [commentProfile, setCommentProfile] = useState<string>();
  const [comments, setComments] = useState<comment[]>([]);
  const token = useApiToken();
  const [loadingComments, setLoadingComments] = useState(true);
  const [roleStatuseses, setRoleStatuses] = useState<RoleStatus[]>([]);
  const [commentType, setCommentType] = useState<string>("");
  const [newCommentType, setNewCommentType] = useState<string>("");

  const graphToken = useGraphApiToken();
  const { username, userId } = useUserData();

  useEffect(() => {
    if (!roleStatusId) return;
    setNewCommentType(roleStatusId);
  }, [roleStatusId]);

  useEffect(() => {
    if (!candidateId || !token) return;
    GetCandidateComments(candidateId, token).then((cc) => {
      setCandidateComments(cc);
    });
    if (roleId)
      GetStatusForRole(roleId, token).then((sr) =>
        setRoleStatuses(sr.roleStatuses.sort((a, b) => a.order - b.order))
      );
    if (!graphToken) return;
    GetProfilePhoto(graphToken).then((pr) => {
      if (pr) setCommentProfile(URL.createObjectURL(pr));
    });
  }, [candidateId, token, graphToken]);

  useEffect(() => {
    setLoadingComments(true);
    const setCommentWithProfile = async () => {
      candidateComments.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      );
      const commentMappedWithImage = await Promise.all(
        candidateComments.map(async (el) => {
          let name: any;
          let profile: Blob | undefined;
          if (graphToken) {
            name = await GetUserName(el.personId, graphToken);
            profile = await GetProfilePhoto(graphToken, el.personId);
          }
          return {
            id: el.id,
            name: name.displayName,
            date: dateConverter(el.date, true),
            personId: el.personId,
            imageSrc: profile && URL.createObjectURL(profile),
            roleStatusId: el.roleStatusId,
            body: el.comment,
          };
        })
      );
      setComments(commentMappedWithImage);
      setLoadingComments(false);
    };
    setCommentWithProfile();
  }, [candidateComments, graphToken]);

  const filteredComments = useMemo(() => {
    if (commentType === "") return comments;
    return comments.filter((c) => c.roleStatusId === commentType);
  }, [comments, commentType]);

  const handleCreateComment = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    const personId = userId;
    if (candidateId && roleId && note && personId && token) {
      let body: CandidateComment = {
        id: uuidv4(),
        candidateId,
        roleId,
        comment: note,
        personId,
        date: new Date().toISOString(),
      };
      if (newCommentType !== "") {
        body = { ...body, roleStatusId: newCommentType };
      }
      CreateCandidateComment(body, token).then((ncc) => {
        const newComment: comment = {
          id: ncc.id,
          name: username ?? "",
          date: dateConverter(ncc.date, true),
          imageSrc: commentProfile,
          roleStatusId: ncc.roleStatusId,
          body: ncc.comment,
          personId: ncc.personId,
        };
        success("Added Successfully", "Note has been added successfully");
        setComments((prev) => [...prev, newComment]);
        setNote("");
      });
    }
  };

  const handleDeleteComment = (id: string) => {
    if (!token) return;
    DeleteComment(id, token).then(() => {
      const newCC = filteredComments.filter((cc) => cc.id !== id);
      setComments(newCC);
    });
  };

  return (
    <section aria-labelledby="notes-title" className="space-y-4">

      {/* ── Notes list card ─────────────────────────────────────── */}
      <div className="bg-white dark:bg-darkbglight rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h2 id="notes-title" className="text-base font-semibold text-gray-900 dark:text-gray-100">
            Notes
          </h2>
          <SelectRoleStatus
            commentType={commentType}
            handleCommentType={(c) => setCommentType(c)}
          />
        </div>

        {/* Comments */}
        <div className="px-6 py-5">
          {loadingComments ? (
            <div className="flex justify-center py-8">
              <Spinner />
            </div>
          ) : filteredComments.length === 0 ? (
            <p className="text-sm text-center text-gray-400 py-6">No notes to display</p>
          ) : (
            <ul className="space-y-0 divide-y divide-gray-100 dark:divide-gray-700 -mx-6">
              {filteredComments.map((comment) => (
                <li key={comment.id} className="px-6 py-5 hover:bg-gray-50 dark:hover:bg-gray-800/30 transition-colors">
                  <div className="flex gap-3">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      <Avatar
                        height="h-9"
                        width="w-9"
                        src={comment.imageSrc}
                        avatarName={getAvatarName(comment?.name)}
                      />
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      {/* Name row */}
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                          {comment.name}
                        </span>
                        {/* Delete controls */}
                        {comment.personId === userId && (
                          deleteCommentConfirm === comment.id ? (
                            <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                              <span className="hidden sm:block">Delete this note?</span>
                              <button onClick={() => setDeleteCommentConfirm("")}>
                                <XMarkIcon className="h-4 w-4 text-red-500 hover:text-red-700" />
                              </button>
                              <button onClick={() => handleDeleteComment(comment.id)}>
                                <CheckIcon className="h-4 w-4 text-entntblue hover:text-entntorange" />
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteCommentConfirm(comment.id)}
                              className="text-gray-300 hover:text-red-500 transition-colors"
                            >
                              <TrashIcon className="h-4 w-4" />
                            </button>
                          )
                        )}
                      </div>

                      {/* Body */}
                      <div className="mt-1 text-sm text-gray-600 dark:text-gray-300 whitespace-pre-wrap">
                        {comment.body}
                      </div>

                      {/* Meta */}
                      <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1">
                        {comment.roleStatusId && (
                          <span className="text-xs font-medium text-indigo-600 dark:text-indigo-400">
                            {roleStatuseses.find((rs) => rs.id === comment.roleStatusId)?.name}
                          </span>
                        )}
                        <span className="text-xs text-gray-400 dark:text-gray-500">
                          {comment.date}
                        </span>
                      </div>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {/* ── Add note card ────────────────────────────────────────── */}
      <div className="bg-white dark:bg-darkbglight rounded-2xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
          <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Add note</h3>
        </div>
        <div className="px-6 py-5">
          <div className="flex gap-3">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <Avatar
                src={commentProfile}
                height="h-9"
                width="w-9"
                avatarName={username && getAvatarName(username)}
              />
            </div>

            {/* Input area */}
            <div className="flex-1 min-w-0">
              <textarea
                id="comment"
                name="comment"
                rows={4}
                className="block w-full rounded-xl border-0 py-3 px-4 text-sm text-gray-900 dark:text-gray-100 bg-gray-50 dark:bg-gray-800 ring-1 ring-inset ring-gray-200 dark:ring-gray-600 placeholder:text-gray-400 focus:ring-2 focus:ring-entntblue focus:bg-white dark:focus:bg-gray-700 transition-all resize-none"
                placeholder="Write a note..."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
              <div className="mt-3 flex items-center justify-end gap-2">
                <SelectRoleStatus
                  commentType={newCommentType}
                  handleCommentType={(c) => setNewCommentType(c)}
                />
                <button
                  type="button"
                  onClick={handleCreateComment}
                  disabled={note.trim() === ""}
                  className="inline-flex items-center px-5 py-2 rounded-xl bg-entntblue text-white text-sm font-semibold hover:bg-entntorange transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-entntblue"
                >
                  Save
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

    </section>
  );
}